import {formatConfidence} from '../WorkforceProfiles/utils';
import type {ApiWorkforceProfileRecord, ApiExtractedSkillRow} from '../WorkforceProfiles/workforceProfileApiTypes';

import type {
  WorkforceProfileAnalysisDataInput,
  WorkforceProfileExtractedCandidateProfile,
  WorkforceProfileExtractedLanguage,
  WorkforceProfileExtractedSkill,
  WorkforceProfileExtractedWorkExperience,
} from '../../types/WorkforceProfileTypes';


const mapExtractedSkills = (rows: ApiExtractedSkillRow[] | undefined): WorkforceProfileExtractedSkill[] =>
  (rows ?? [])
    .filter((row): row is ApiExtractedSkillRow & {skill: string} => typeof row.skill === 'string')
    .map((row) => ({
      skill: row.skill,
      evidence: String(row.evidence ?? '').trim(),
      confidence: formatConfidence(row.confidence),
    }));

const mapExtractedLanguages = (
  rows: Array<{language?: string; level?: string; evidence?: string}> | undefined
): WorkforceProfileExtractedLanguage[] =>
  (rows ?? [])
    .filter((row): row is {language: string; level?: string; evidence?: string} => typeof row.language === 'string')
    .map((row) => ({
      language: row.language,
      level: String(row.level ?? '').trim(),
      evidence: String(row.evidence ?? '').trim(),
    }));

const mapExtractedWorkExperiences = (
  rows:
    | Array<{
        roleTitle?: string;
        organizationDescriptor?: string;
        duration?: string;
        highlights?: string[];
      }>
    | undefined
): WorkforceProfileExtractedWorkExperience[] =>
  (rows ?? [])
    .filter(
      (
        row
      ): row is {
        roleTitle: string;
        organizationDescriptor?: string;
        duration?: string;
        highlights?: string[];
      } => typeof row.roleTitle === 'string'
    )
    .map((row) => ({
      roleTitle: row.roleTitle,
      organizationDescriptor: String(row.organizationDescriptor ?? '').trim(),
      duration: String(row.duration ?? '').trim(),
      highlights: Array.isArray(row.highlights) ? (row.highlights as string[]) : [],
    }));

const mapExtractedCandidateProfile = (
  profile: ApiWorkforceProfileRecord | undefined
): WorkforceProfileExtractedCandidateProfile | undefined => {
  if (!profile) {
    return undefined;
  }

  const fields = {
    summary: (profile.summary as string | undefined)?.trim() || null,
    education: (profile.education as string | undefined)?.trim() || null,
    responsibilities: (profile.responsibilities as string | undefined)?.trim() || null,
    senioritySignals: (profile.senioritySignals as string | undefined)?.trim() || null,
    industryExperience: (profile.industryExperience as string | undefined)?.trim() || null,
    workingStyleNotes: (profile.workingStyleNotes as string | undefined)?.trim() || null,
  };

  if (!Object.values(fields).some(Boolean)) {
    return undefined;
  }

  return fields;
};

export const mapProfileAnalysisDataInput = (
  raw: ApiWorkforceProfileRecord | undefined
): WorkforceProfileAnalysisDataInput | undefined => {
  if (!raw) {
    return undefined;
  }

  const candidateProfile = mapExtractedCandidateProfile(raw.candidateProfile as ApiWorkforceProfileRecord | undefined);
  const skillsCompetencies = mapExtractedSkills(raw.skillsCompetencies as ApiExtractedSkillRow[] | undefined);
  const knowledgeSkills = mapExtractedSkills(raw.knowledgeSkills as ApiExtractedSkillRow[] | undefined);
  const workExperiences = mapExtractedWorkExperiences(
    raw.workExperiences as Parameters<typeof mapExtractedWorkExperiences>[0]
  );
  const languages = mapExtractedLanguages(raw.languages as Parameters<typeof mapExtractedLanguages>[0]);
  const certifications = Array.isArray(raw.certifications) ? (raw.certifications as string[]) : [];
  const gapsOrAmbiguities = Array.isArray(raw.gapsOrAmbiguities) ? (raw.gapsOrAmbiguities as string[]) : [];
  const extractionConfidence = formatConfidence(raw.extractionConfidence as string | undefined);

  const hasContent =
    Boolean(extractionConfidence) ||
    Boolean(candidateProfile) ||
    skillsCompetencies.length > 0 ||
    knowledgeSkills.length > 0 ||
    workExperiences.length > 0 ||
    languages.length > 0 ||
    certifications.length > 0 ||
    gapsOrAmbiguities.length > 0;

  if (!hasContent) {
    return undefined;
  }

  return {
    extractionConfidence,
    candidateProfile,
    skillsCompetencies,
    knowledgeSkills,
    workExperiences,
    languages,
    certifications,
    gapsOrAmbiguities,
  };
};
